import React, { useId } from "react";
import { Controller } from "react-hook-form";
import { Button } from "../../../../shared/components/ui/Button";
import { LoadingSpinner } from "../../../../shared/components/ui/LoadingSpinner";
import { FormField } from "../../../../shared/components/forms/FormField";
import { useManagementReportGenerator } from "./useManagementReportGenerator";
import type { BuildingOption } from "../../../../api/client/types/common.types";
import { DateRangePicker } from "../DateRangePicker/DateRangePicker";
import {
  LABEL_DATE_RANGE,
  LABEL_BUILDING_REQUIRED,
  PLACEHOLDER_SEARCH_BUILDING,
  PLACEHOLDER_LOADING_BUILDINGS,
  LOADING_BUILDINGS,
  NO_BUILDINGS_AVAILABLE,
  CHECKBOX_INCLUDE_YEAR_SUMMARY,
  BUTTON_GENERATING,
  BUTTON_GENERATE_INFORM,
  NOTE_LABEL,
} from "../../constants/reportMessages";

interface ManagementReportGeneratorProps {
  title: string;
  description: string;
  onGenerate: (
    start_date: string,
    end_date: string,
    building_id: string,
    includeYearSummary: boolean
  ) => Promise<Blob | null>;
  /** Full list of all buildings (not filtered by date). */
  buildingOptions: BuildingOption[];
  /** Optional: true while the building list is loading. */
  isFetchingBuildings?: boolean;
  /** Optional badge shown next to the title (e.g. "Beta"). Remove once legacy is retired. */
  badge?: string;
}

/**
 * Report generator for the management report PDF (informe de gestión).
 * Uses react-hook-form with DateRangePicker, required building selector, and FormField checkbox.
 */
export const ManagementReportGenerator: React.FC<ManagementReportGeneratorProps> = ({
  title,
  description,
  onGenerate,
  buildingOptions,
  isFetchingBuildings = false,
  badge,
}) => {
  const uid = useId();

  const {
    form,
    startDate,
    endDate,
    errors,
    isValid,
    isSubmitting,
    handleDateRangeChange,
    handleGenerateReport,
  } = useManagementReportGenerator({ onGenerate, buildingOptions });

  const buildingSelectOptions: { value: string; label: string }[] =
    buildingOptions.map((b) => ({ value: b.id, label: b.name }));

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
        {badge && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
            {badge}
          </span>
        )}
      </div>

      <div className="space-y-6">
        <div className="space-y-4">
          <DateRangePicker
            id={`${uid}-date-range`}
            label={LABEL_DATE_RANGE}
            required
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateRangeChange}
            maxRangeDays={32}
            errorMessages={[errors.start_date, errors.end_date].filter(Boolean) as string[]}
          />

          <div className="max-w-md">
            <Controller
              control={form.control}
              name="building_id"
              render={({ field }) => (
                <FormField
                  type="searchableSelect"
                  label={LABEL_BUILDING_REQUIRED}
                  searchableSelectProps={{
                    id: `${uid}-building`,
                    options: buildingSelectOptions,
                    value: field.value,
                    onChange: field.onChange,
                    placeholder: isFetchingBuildings
                      ? PLACEHOLDER_LOADING_BUILDINGS
                      : PLACEHOLDER_SEARCH_BUILDING,
                    disabled: isFetchingBuildings,
                  }}
                  error={errors.building_id}
                  required
                  className="gap-0"
                />
              )}
            />
            {isFetchingBuildings && (
              <div className="mt-1 text-sm text-blue-600 flex items-center gap-2">
                <LoadingSpinner size="sm" />
                {LOADING_BUILDINGS}
              </div>
            )}
            {!isFetchingBuildings && buildingOptions.length === 0 && !errors.building_id && (
              <p className="mt-1 text-sm text-gray-500">{NO_BUILDINGS_AVAILABLE}</p>
            )}
          </div>

          <FormField
            type="checkbox"
            label=""
            checkboxProps={{
              control: form.control,
              name: "includeYearSummary",
              label: CHECKBOX_INCLUDE_YEAR_SUMMARY,
              id: `${uid}-include-year-summary`,
            }}
          />
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={handleGenerateReport}
            disabled={isSubmitting || !isValid}
            className="w-full sm:w-auto min-w-[200px]"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <LoadingSpinner size="sm" />
                {BUTTON_GENERATING}
              </span>
            ) : (
              BUTTON_GENERATE_INFORM
            )}
          </Button>
        </div>
      </div>

      <div className="mt-4 p-4 bg-blue-50 rounded-md">
        <p className="text-sm text-gray-700">
          <strong>{NOTE_LABEL}:</strong> {description}
        </p>
      </div>
    </div>
  );
};
